import { Link as RouterLink } from 'react-router-dom';
import { Stack, Table, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Card } from '@liminal/web-ui/primitives/Card';
import { Link } from '@liminal/web-ui/primitives/Link';
import { DataTableBody } from '@liminal/web-ui/display/DataTableBody';
import { Select } from '@liminal/web-ui/forms/Select';
import { Snackbar } from '@liminal/web-ui/feedback/Snackbar';
import { useSnackbar } from '@liminal/web-ui/feedback/useSnackbar';
import { CursorPagination } from '@liminal/web-ui/primitives/Pagination';
import { useCursorPagination } from '@liminal/web-ui/primitives/useCursorPagination';
import { fontSizes } from '@liminal/brand/scale';
import { formatOrderDate } from '@liminal/utils/date';
import { ConversationFlag_Source, ConversationFlag_State } from '@/api/generated';
import type { ConversationFlag } from '@/api/types';
import { UserIdLink } from '@/components/ui/UserIdLink';
import { conversationFlagSourceLabel, conversationFlagState } from '@/components/ui/statusHelpers';
import { getErrorMessage } from '@/errors/messages';
import { useConversationFlags, useUpdateConversationFlag } from '@/hooks/useConversationFlags';
import { useUrlState } from '@/hooks/useUrlState';
import { conversationFlagHref } from '../components/conversationFlags';

const PAGE_SIZE = 50;

const COLUMNS = ['Feedback', 'Flagged', 'User', 'Reported by', 'Raised', 'State'] as const;

const STATES = Object.values(ConversationFlag_State);

const STATE_OPTIONS = STATES.map((state) => ({ value: state, label: conversationFlagState(state).label }));

const STATE_FILTER_OPTIONS = [...STATE_OPTIONS, { value: '', label: 'Any state' }];

// Open by default: the page is the queue of flags nobody has looked at yet.
const FILTER_DEFAULTS: { state: string } = { state: ConversationFlag_State.STATE_OPEN };

// Feedback and replies run long, so a row shows their opening lines; the
// feedback's whole text is on hover, and the reply's a click away in context.
const FeedbackPreview = styled(Typography)({
  display: '-webkit-box',
  WebkitLineClamp: 4,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  whiteSpace: 'pre-wrap',
});

const ReplyPreview = styled(Typography)({
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
});

/**
 * The team's flags on Nim's replies, newest first: what Nim got wrong, on
 * which reply, and where each flag is in review. A flag is raised from a
 * conversation's page, on a reply or on the whole conversation.
 */
export function FlaggedPage() {
  const [filters, setFilters] = useUrlState(FILTER_DEFAULTS);
  const pagination = useCursorPagination(PAGE_SIZE);
  const snackbar = useSnackbar();
  const updateFlag = useUpdateConversationFlag();

  const state = STATES.find((candidate) => candidate === filters.state);
  // A hand-edited link naming no state is refused here, not sent.
  const unknownState = filters.state !== '' && state === undefined;

  const flagsQuery = useConversationFlags({
    state,
    pageSize: PAGE_SIZE,
    pageToken: pagination.pageToken ?? '',
    enabled: !unknownState,
  });
  const flags = flagsQuery.data?.flags ?? [];
  const nextPageToken = flagsQuery.data?.nextPageToken ?? '';

  const errorMessage = unknownState
    ? `Flags cannot be filtered by "${filters.state}".`
    : flagsQuery.isError
      ? `Could not load flags: ${getErrorMessage(flagsQuery.error)}`
      : '';

  const changeState = (flag: ConversationFlag, value: string) => {
    const next = STATES.find((candidate) => candidate === value);
    if (next === undefined) throw new Error(`Conversation flag cannot move to the unknown state ${value}`);
    updateFlag.mutate(
      { flagId: flag.id, state: next },
      {
        onSuccess: (updated) => snackbar.show(`Flag marked ${conversationFlagState(updated.state).label.toLowerCase()}.`),
        onError: (error) => snackbar.show(`Could not update the flag: ${getErrorMessage(error)}`, 'error'),
      },
    );
  };

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
        <Select
          variant="filter"
          aria-label="Flag state"
          size="sm"
          width={200}
          value={filters.state}
          onChange={(next) => {
            setFilters({ state: next });
            pagination.reset();
          }}
          options={STATE_FILTER_OPTIONS}
        />
        <Typography variant="body2" color="text.disabled">
          Flag a reply from its conversation: hover it and press the flag.
        </Typography>
      </Stack>

      {/* Newest first, as the server orders them; there is no sort to apply. */}
      <Card variant="elevated" padding="none">
        <TableContainer>
          <Table size="small" sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow>
                {COLUMNS.map((column) => (
                  <TableCell key={column}>{column}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <DataTableBody
              columns={COLUMNS.length}
              isLoading={flagsQuery.isLoading}
              isError={flagsQuery.isError || unknownState}
              isEmpty={flags.length === 0}
              loadingRows={8}
              emptyMessage={state === ConversationFlag_State.STATE_OPEN ? 'No open flags. Nothing waits for review.' : 'No flags here.'}
              errorMessage={errorMessage}
            >
              {flags.map((flag) => {
                const updating = updateFlag.isPending && updateFlag.variables?.flagId === flag.id;
                return (
                  <TableRow key={flag.id} hover data-testid={`conversation-flag-${flag.id}`}>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <FeedbackPreview variant="body2" title={flag.feedback}>
                        {flag.feedback}
                      </FeedbackPreview>
                      {flag.reviewNotes && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Review: {flag.reviewNotes}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <ReplyPreview variant="body2">
                        <Link component={RouterLink} to={conversationFlagHref(flag)}>
                          {flag.messageId ? flag.messageContent || 'The flagged reply' : 'The whole conversation'}
                        </Link>
                      </ReplyPreview>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {flag.conversationTitle || 'Untitled'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <UserIdLink userId={flag.userId} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: fontSizes.xs }}>
                        {flag.reportedBy}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {conversationFlagSourceLabel(flag.source)}
                        {flag.sourceUrl && (
                          <>
                            {' · '}
                            <Link href={flag.sourceUrl} external>
                              {flag.source === ConversationFlag_Source.SOURCE_SLACK ? 'Slack thread' : 'Source'}
                            </Link>
                          </>
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontSize: fontSizes.xs }}>
                      {formatOrderDate(flag.createTime)}
                    </TableCell>
                    <TableCell>
                      <Select
                        variant="filter"
                        aria-label="State"
                        size="sm"
                        width={150}
                        value={flag.state}
                        onChange={(value) => changeState(flag, value)}
                        options={STATE_OPTIONS}
                        disabled={updating}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </DataTableBody>
          </Table>
        </TableContainer>
      </Card>

      <CursorPagination
        firstRowNumber={pagination.firstRowNumber}
        count={flags.length}
        hasPreviousPage={pagination.hasPreviousPage}
        hasNextPage={!!nextPageToken}
        onPreviousPage={pagination.goToPreviousPage}
        onNextPage={() => pagination.goToNextPage(nextPageToken)}
        disabled={flagsQuery.isLoading}
      />

      <Snackbar {...snackbar.snackbarProps} />
    </Stack>
  );
}
